// Products catalogue — Phase 2 of the products/upsells work (2026-08-01).
//
// Products are created in SHOPIFY ONLY. This page does not create anything; it
// mirrors the Shopify catalogue and captures the two judgements Shopify cannot
// hold for us:
//   * Utilisé  — does the system actually use this product?
//   * Type     — Offre (the album formula) or Upsell (a paid add-on)?
// Add a product in Shopify, sync, tick it, categorise it. No code change — which
// is the point: every upsell used to need its own boolean column in the database.
//
// READ-ONLY vs OURS. Title, price and image come from Shopify and are shown but
// never editable: Shopify does the billing, so a local copy could only ever
// drift from what a customer is really charged. "Coût de production" is the
// opposite — Shopify has no idea what a thing costs US to make, so that field is
// ours and feeds margin only. It is NEVER charged to anyone.
//
// Design language: admin redesign (clusters, gold accents, Fraunces headings).

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi, type CatalogueProduct } from '@/lib/api';
import { ADM_COLORS as COLORS } from '@/lib/brand';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';

const GOLD = COLORS.accentPrimary;
const GOLD_DARK = 'var(--color-adm-gold-ink)'; // #8B6F26 in light
const SERIF = "'Fraunces', Georgia, serif";

const fmtMad = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(Math.round(Number(n) * 100) / 100).toLocaleString('fr-FR')} MAD`;

/**
 * Shopify CDN thumbnail. The existing `pickerThumbUrl` is Supabase-only and
 * passes Shopify URLs straight through at FULL size — with 60+ products that
 * would pull multi-megabyte originals into a list of 56px squares. Shopify
 * resizes on its own CDN, so ask it for what we actually display.
 */
function shopifyThumb(url: string | null, width = 112): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.endsWith('shopify.com') && !u.hostname.endsWith('shopifycdn.com')) return url;
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

function SectionHead({ title, subtitle, action }: {
  title: string; subtitle?: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 flex-wrap mb-4">
      <div className="min-w-0 flex-1">
        <h2 className="text-xl" style={{ fontFamily: SERIF, color: GOLD_DARK }}>{title}</h2>
        {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
        <div className="h-px mt-2" style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
      </div>
      {action}
    </div>
  );
}

/** Small pill used for Offre / Upsell / not-categorised. */
function KindPill({ kind }: { kind: CatalogueProduct['kind'] }) {
  const map = {
    offer:  { label: 'Offre',  cls: 'bg-[#EEF6EE] text-[#2F6B33] border-[#CBE3CD] dark:bg-status-done-bg dark:text-status-done dark:border-status-done-border' },
    upsell: { label: 'Upsell', cls: 'bg-[#FDF3E2] text-adm-gold-ink border-[#EBD9B4] dark:bg-adm-gold-wash dark:border-adm-gold/40' },
  } as const;
  if (!kind) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full border bg-[#F5F5F4] text-[#78716C] border-[#E7E5E4] dark:bg-adm-surface-3 dark:text-adm-muted dark:border-adm-line">
        À classer
      </span>
    );
  }
  const s = map[kind];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ProductRow({ product }: { product: CatalogueProduct }) {
  const qc = useQueryClient();
  const [costDraft, setCostDraft] = useState<string>(
    product.production_cost === null || product.production_cost === undefined ? '' : String(product.production_cost),
  );
  const [open, setOpen] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['products'] });
  const update = useMutation({
    mutationFn: (data: Parameters<typeof productsApi.update>[1]) => productsApi.update(product.id, data),
    onSuccess: invalidate,
  });

  const thumb = shopifyThumb(product.image_url);
  const priceRange = useMemo(() => {
    const prices = product.variants.map((v) => Number(v.price)).filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length === 0) return '—';
    const lo = Math.min(...prices), hi = Math.max(...prices);
    return lo === hi ? fmtMad(lo) : `${fmtMad(lo)} – ${fmtMad(hi)}`;
  }, [product.variants]);

  return (
    <div className={`border rounded-xl bg-white overflow-hidden ${product.enabled
      ? 'border-[#EBD9B4] dark:border-adm-gold/40'
      : 'border-[#E7E5E4] dark:border-adm-line'}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-14 h-14 rounded-lg bg-neutral-100 shrink-0 overflow-hidden grid place-items-center" data-adm-media>
          {thumb
            ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
            : <span className="text-neutral-300 text-xs">—</span>}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{product.title ?? '(sans titre)'}</span>
            <KindPill kind={product.kind} />
            {product.status && product.status !== 'ACTIVE' && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-500">
                {product.status}
              </span>
            )}
          </div>
          <div className="text-sm text-neutral-500 mt-0.5">
            {priceRange}
            <span className="mx-2 text-neutral-300 dark:text-adm-faint">·</span>
            {product.variants.length} variante{product.variants.length > 1 ? 's' : ''}
            {product.production_cost !== null && product.production_cost !== undefined && (
              <>
                <span className="mx-2 text-neutral-300 dark:text-adm-faint">·</span>
                coût {fmtMad(product.production_cost)}
              </>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm shrink-0 cursor-pointer">
          <input
            type="checkbox"
            checked={product.enabled}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ enabled: e.target.checked })}
            className="w-4 h-4 accent-[#C9A04C]"
          />
          <span className="hidden sm:inline text-neutral-600">Utilisé</span>
        </label>

        <button type="button" onClick={() => setOpen((v) => !v)}
                className="text-sm px-2 py-1 rounded-lg border border-neutral-200 text-neutral-600 shrink-0">
          {open ? 'Fermer' : 'Détails'}
        </button>
      </div>

      {open && (
        <div className="border-t px-3 py-3 bg-neutral-50/60 border-[#F0EEEA] dark:border-adm-line">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Type</label>
              <select
                value={product.kind ?? ''}
                disabled={update.isPending}
                onChange={(e) => update.mutate({ kind: (e.target.value || null) as 'offer' | 'upsell' | null })}
                className="border rounded-lg px-2 py-1.5 text-sm bg-white border-[#E7E5E4] dark:border-adm-line-strong"
              >
                <option value="">À classer</option>
                <option value="offer">Offre (formule album)</option>
                <option value="upsell">Upsell (option payante)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-neutral-500 mb-1">
                Coût de production (MAD)
              </label>
              <div className="flex gap-2">
                <input
                  type="number" min="0" step="0.01" inputMode="decimal"
                  value={costDraft}
                  placeholder="inconnu"
                  onChange={(e) => setCostDraft(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm w-32 bg-white border-[#E7E5E4] dark:border-adm-line-strong"
                />
                <Button
                  size="sm" variant="secondary"
                  disabled={update.isPending}
                  onClick={() => update.mutate({
                    production_cost: costDraft.trim() === '' ? null : Number(costDraft),
                  })}
                >
                  Enregistrer
                </Button>
              </div>
              <p className="text-xs text-neutral-400 mt-1">
                Ce que ça nous coûte à fabriquer. Sert à la marge — jamais facturé au client.
              </p>
            </div>
          </div>

          {product.variants.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-neutral-500 mb-2">
                Variantes — prix de vente lu depuis Shopify (non modifiable ici)
              </div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <div key={v.id}
                       className="border rounded-lg px-3 py-2 bg-white text-sm border-[#E7E5E4] dark:border-adm-line">
                    <div className="font-medium">{v.title ?? '—'}</div>
                    <div className="text-neutral-500">{fmtMad(v.price)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminProductsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'used' | 'todo'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list(),
  });

  const sync = useMutation({
    mutationFn: () => productsApi.sync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });

  const all = data?.products ?? [];
  const shown = useMemo(() => {
    if (filter === 'used') return all.filter((p) => p.enabled);
    if (filter === 'todo') return all.filter((p) => p.enabled && !p.kind);
    return all;
  }, [all, filter]);

  const usedCount = all.filter((p) => p.enabled).length;
  const todoCount = all.filter((p) => p.enabled && !p.kind).length;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        <SectionHead
          title="Produits"
          subtitle="Les produits se créent dans Shopify. Ici on choisit ceux que le système utilise et on indique si c'est une offre ou un upsell."
          action={
            <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
              {sync.isPending ? 'Synchronisation…' : 'Synchroniser Shopify'}
            </Button>
          }
        />

        {sync.isSuccess && (
          <div className="mb-4 text-sm rounded-lg px-3 py-2 border bg-[#FDF3E2] border-[#EBD9B4] text-adm-gold-ink dark:bg-adm-gold-wash dark:border-adm-gold/40">
            {sync.data.products} produit(s) et {sync.data.variants} variante(s) synchronisés.
          </div>
        )}
        {sync.isError && (
          <div className="mb-4 text-sm rounded-lg px-3 py-2 border border-red-200 bg-red-50 text-red-700">
            Synchronisation impossible : {(sync.error as Error).message}
            <div className="text-xs mt-1 text-red-500">
              Le jeton Shopify expire toutes les 24 h — il faut peut-être le renouveler.
            </div>
          </div>
        )}

        {/* Anything ticked but not categorised is invisible to the rest of the
            system, so surface the count rather than let it sit silently. */}
        {todoCount > 0 && (
          <button type="button" onClick={() => setFilter('todo')}
                  className="mb-4 w-full text-left text-sm rounded-lg px-3 py-2 border bg-[#FFFBF2] border-[#EBD9B4] text-adm-gold-ink dark:bg-adm-gold-wash dark:border-adm-gold/40">
            {todoCount} produit(s) utilisé(s) mais pas encore classé(s) en Offre ou Upsell — cliquez pour les voir.
          </button>
        )}

        <div className="flex gap-2 mb-4 text-sm">
          {([
            ['all', `Tous (${all.length})`],
            ['used', `Utilisés (${usedCount})`],
            ['todo', `À classer (${todoCount})`],
          ] as const).map(([key, label]) => (
            <button
              key={key} type="button" onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full border ${filter === key
                ? 'text-white'
                : 'bg-white border-[#E7E5E4] text-[#57534E] dark:border-adm-line dark:text-adm-ink-2'}`}
              style={filter === key ? { background: GOLD, borderColor: GOLD } : undefined}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading && <div className="text-neutral-500 text-sm">Chargement…</div>}
        {error && (
          <div className="text-sm rounded-lg px-3 py-2 border border-red-200 bg-red-50 text-red-700">
            {(error as Error).message}
          </div>
        )}

        {!isLoading && !error && all.length === 0 && (
          <div className="text-center py-12 border rounded-xl bg-white border-[#E7E5E4] dark:border-adm-line">
            <p className="text-neutral-600">Aucun produit dans le catalogue local.</p>
            <p className="text-sm text-neutral-400 mt-1">
              Cliquez sur « Synchroniser Shopify » pour les importer.
            </p>
          </div>
        )}

        <div className="space-y-2">
          {shown.map((p) => <ProductRow key={p.id} product={p} />)}
        </div>

        {!isLoading && all.length > 0 && shown.length === 0 && (
          <div className="text-center py-8 text-sm text-neutral-400">
            Aucun produit dans ce filtre.
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
